/**
 * Unified file wizard session for Lot 6.6B.2 (CSV / XLSX / XLS).
 * Holds importId as soon as the upload ticket exists and cancels
 * best-effort on known upload/parse failures. No retry/resume.
 * Requires-async is informational — the job stays usable for mapping.
 */

async function cancelImportBestEffort(cancelImport, importId) {
  if (!importId || typeof cancelImport !== 'function') return;
  try {
    await cancelImport({ importId });
  } catch {
    /* best-effort; no PII */
  }
}

/**
 * @returns {Promise<{ ok: true, parsed: object, importId: string }>}
 */
export async function runExcelImportSession({
  createUpload,
  uploadSource,
  parseFile,
  cancelImport,
  onImportId,
  onPhase,
  file,
  fileType,
  contentType,
  byteSize,
  parseOptions,
} = {}) {
  let createdImportId = null;
  try {
    const ticket = await createUpload({ fileType, contentType, byteSize });
    createdImportId = ticket?.importId || null;
    if (createdImportId && typeof onImportId === 'function') {
      onImportId(createdImportId);
    }
    if (typeof onPhase === 'function') onPhase('uploading');
    await uploadSource({
      upload: ticket?.upload,
      file,
      contentType,
    });
    if (typeof onPhase === 'function') onPhase('parsing');
    const parsed = await parseFile({
      importId: createdImportId,
      ...(parseOptions && typeof parseOptions === 'object' ? parseOptions : {}),
    });
    return { ok: true, parsed, importId: createdImportId };
  } catch (error) {
    await cancelImportBestEffort(cancelImport, createdImportId);
    throw error;
  }
}

export { runExcelImportSession as runFileImportSession };
